
import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Card from '../../components/base/Card';
import Button from '../../components/base/Button';
import KnowledgeGraphChart from '../../components/feature/KnowledgeGraphChart';

export default function KnowledgeMapPage() {
  const [activeLayer, setActiveLayer] = useState('foundation');
  const [selectedPath, setSelectedPath] = useState('');
  const [selectedNode, setSelectedNode] = useState<any>(null);

  const knowledgeLayers = {
    foundation: {
      title: '核心基础层',
      description: '财税专业的基础理论知识',
      color: 'from-blue-500 to-blue-600',
      nodes: [
        { 
          id: 'accounting-basic', 
          name: '会计基础', 
          category: 0, 
          value: 5,
          description: '会计学基本原理与方法，包括会计要素、会计等式、复式记账等核心概念'
        },
        { 
          id: 'tax-theory', 
          name: '税法理论', 
          category: 0, 
          value: 4,
          description: '税收法律法规基础，税收的基本原则和税法体系结构'
        },
        { 
          id: 'finance-theory', 
          name: '财务管理理论', 
          category: 0, 
          value: 4,
          description: '企业财务管理基本理论，包括资金时间价值、风险与收益等'
        }
      ],
      links: [
        { source: 'accounting-basic', target: 'tax-theory' },
        { source: 'accounting-basic', target: 'finance-theory' }
      ]
    },
    professional: {
      title: '专业核心层',
      description: '各专业领域的核心课程',
      color: 'from-green-500 to-green-600',
      nodes: [
        { 
          id: 'tax-accounting', 
          name: '税务会计', 
          category: 1, 
          value: 4,
          description: '税务相关会计处理，包括各税种的会计核算方法'
        },
        { 
          id: 'management-accounting', 
          name: '管理会计', 
          category: 2, 
          value: 4,
          description: '企业内部管理会计，成本控制与绩效评价'
        },
        { 
          id: 'gov-accounting', 
          name: '行政事业单位会计', 
          category: 2, 
          value: 3,
          description: '政府会计制度，预算会计与财务会计双体系'
        },
        { 
          id: 'tax-planning', 
          name: '纳税筹划合规', 
          category: 4, 
          value: 4,
          description: '合法税务筹划方法与合规管理'
        },
        { 
          id: 'tax-inspection', 
          name: '纳税检查', 
          category: 2, 
          value: 3,
          description: '税务稽查实务，检查方法与程序'
        },
        { 
          id: 'corporate-finance', 
          name: '企业财务管理', 
          category: 3, 
          value: 5,
          description: '企业财务决策，投资、筹资与分配管理'
        },
        { 
          id: 'internal-control', 
          name: '内部控制与风险管理', 
          category: 3, 
          value: 4,
          description: '企业风险控制体系建设与管理'
        },
        { 
          id: 'financial-audit', 
          name: '财务审计', 
          category: 3, 
          value: 4,
          description: '财务审计实务，审计程序与方法'
        }
      ],
      links: [
        { source: 'tax-accounting', target: 'management-accounting' },
        { source: 'tax-accounting', target: 'tax-planning' },
        { source: 'management-accounting', target: 'corporate-finance' },
        { source: 'corporate-finance', target: 'internal-control' },
        { source: 'internal-control', target: 'financial-audit' },
        { source: 'tax-planning', target: 'tax-inspection' }
      ]
    },
    application: {
      title: '技能应用层',
      description: '实践技能与工具应用',
      color: 'from-teal-500 to-teal-600',
      nodes: [
        { 
          id: 'excel-finance', 
          name: 'Excel在财务中的应用', 
          category: 5, 
          value: 3,
          description: '财务数据处理工具，函数应用与数据分析'
        },
        { 
          id: 'big-data-analysis', 
          name: '财税大数据分析', 
          category: 5, 
          value: 5,
          description: '大数据技术在财税领域的应用与分析方法'
        },
        { 
          id: 'tax-filing-training', 
          name: '纳税申报实训', 
          category: 2, 
          value: 4,
          description: '实际申报操作，各税种申报表填写'
        },
        { 
          id: 'tax-admin-training', 
          name: '税收征管流程实训', 
          category: 2, 
          value: 4,
          description: '征管流程实践，税务登记到注销全流程'
        },
        { 
          id: 'smart-tax-filing', 
          name: '智慧化税费申报与管理', 
          category: 5, 
          value: 5,
          description: '智能申报系统操作与管理'
        },
        { 
          id: 'tax-robot', 
          name: '财税机器人开发与维护', 
          category: 5, 
          value: 5,
          description: '自动化工具开发，RPA技术应用'
        }
      ],
      links: [
        { source: 'excel-finance', target: 'big-data-analysis' },
        { source: 'big-data-analysis', target: 'tax-robot' },
        { source: 'tax-filing-training', target: 'smart-tax-filing' },
        { source: 'tax-admin-training', target: 'smart-tax-filing' },
        { source: 'smart-tax-filing', target: 'tax-robot' }
      ]
    },
    integration: {
      title: '综合实践层',
      description: '综合应用与创新实践',
      color: 'from-orange-500 to-red-500',
      nodes: [
        { 
          id: 'smart-finance-tax', 
          name: '智能财税', 
          category: 5, 
          value: 6,
          description: 'AI技术在财税中的应用，智能决策支持系统'
        },
        { 
          id: 'business-finance-system', 
          name: '业财管理信息系统应用', 
          category: 5, 
          value: 4,
          description: '企业信息系统集成，业务财务一体化'
        },
        { 
          id: 'tax-management', 
          name: '税务管理', 
          category: 2, 
          value: 5,
          description: '税务综合管理，政策解读与实务应用'
        },
        { 
          id: 'labor-concept', 
          name: '劳动观念', 
          category: 0, 
          value: 3,
          description: '新时代劳动教育，培养正确劳动价值观'
        },
        { 
          id: 'labor-skills', 
          name: '劳动技能', 
          category: 0, 
          value: 3,
          description: '实践技能培养，动手能力提升'
        }
      ],
      links: [
        { source: 'smart-finance-tax', target: 'business-finance-system' },
        { source: 'smart-finance-tax', target: 'tax-management' },
        { source: 'labor-concept', target: 'labor-skills' }
      ]
    }
  };

  const categories = [
    { name: '基础概念', color: '#3B82F6' },
    { name: '税收分类', color: '#10B981' },
    { name: '税务管理', color: '#F59E0B' },
    { name: '纳税义务', color: '#EF4444' },
    { name: '税务筹划', color: '#8B5CF6' },
    { name: '发展趋势', color: '#06B6D4' }
  ];

  const learningPaths = [
    {
      id: 'traditional-to-smart',
      name: '传统财税到智能财税',
      description: '从基础理论到智能应用的完整路径',
      steps: ['会计基础', '税务会计', '纳税申报实训', '智慧化税费申报与管理', '智能财税'],
      color: 'bg-blue-500'
    },
    {
      id: 'financial-management',
      name: '财务管理到风险控制',
      description: '财务管理与企业风险控制的核心联系',
      steps: ['会计基础', '企业财务管理', '企业内部控制与风险管理', '财务审计'],
      color: 'bg-purple-500'
    },
    {
      id: 'technical-skills',
      name: '技术技能发展路径',
      description: '从基础工具到自动化技术的技能提升',
      steps: ['Excel在财务中的应用', '财税大数据分析', '财税机器人开发与维护'],
      color: 'bg-teal-500'
    },
    {
      id: 'tax-management',
      name: '征管实务发展路径',
      description: '税收征管的数字化与智能化发展',
      steps: ['税收征管流程实训', '税务管理', '业财管理信息系统应用'],
      color: 'bg-green-500'
    }
  ];

  const currentLayer = knowledgeLayers[activeLayer as keyof typeof knowledgeLayers];

  const handleNodeClick = (node: any) => {
    setSelectedNode(node);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="py-16 bg-gradient-to-br from-blue-50 to-indigo-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                财税大数据专业知识图谱
              </h1>
              <p className="text-xl text-gray-600 max-w-4xl mx-auto mb-8">
                完整展示财税大数据专业的四层知识体系结构，清晰呈现知识关联与学习路径，
                帮助学生系统掌握从基础理论到综合实践的全方位专业技能
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Button>
                  <i className="ri-download-line mr-2"></i>
                  下载图谱
                </Button>
                <Button variant="outline">
                  <i className="ri-share-line mr-2"></i>
                  分享图谱
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Interactive Knowledge Map */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
              {/* Layer Navigation */}
              <div className="lg:col-span-1">
                <Card>
                  <h3 className="text-xl font-bold text-gray-900 mb-6">知识层级导航</h3>
                  <div className="space-y-3">
                    {Object.entries(knowledgeLayers).map(([key, layer]) => (
                      <button
                        key={key}
                        onClick={() => setActiveLayer(key)}
                        className={`w-full text-left p-4 rounded-lg transition-all ${
                          activeLayer === key
                            ? 'bg-gradient-to-r ' + layer.color + ' text-white shadow-lg'
                            : 'bg-gray-50 hover:bg-gray-100 text-gray-700'
                        }`}
                      >
                        <div className="font-semibold">{layer.title}</div>
                        <div className="text-sm mt-1 opacity-75">{layer.description}</div>
                      </button>
                    ))}
                  </div>
                </Card>

                {/* Layer Statistics */}
                <Card className="mt-6">
                  <h4 className="font-semibold text-gray-900 mb-4">当前层级统计</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">课程数量</span>
                      <span className="font-semibold">{currentLayer.nodes.length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">知识点</span>
                      <span className="font-semibold">{currentLayer.nodes.length * 3}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">学习路径</span>
                      <span className="font-semibold">4条</span>
                    </div>
                  </div>
                </Card>

                {/* 图例说明 */}
                <Card className="mt-6">
                  <h4 className="font-semibold text-gray-900 mb-4">知识分类</h4>
                  <div className="space-y-2">
                    {categories.map((category, index) => (
                      <div key={category.name} className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: category.color }}
                        ></div>
                        <span className="text-sm text-gray-600">{category.name}</span>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>

              {/* Main Visualization */}
              <div className="lg:col-span-3">
                <Card>
                  <div className="flex justify-between items-center mb-6">
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900">{currentLayer.title}</h3>
                      <p className="text-gray-600 mt-1">{currentLayer.description}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm">
                        <i className="ri-screenshot-line mr-2"></i>
                        导出图片
                      </Button>
                    </div>
                  </div>
                  
                  {/* ECharts 知识图谱 */}
                  <div className="rounded-lg overflow-hidden border border-gray-200">
                    <KnowledgeGraphChart
                      nodes={currentLayer.nodes}
                      links={currentLayer.links}
                      categories={categories}
                      onNodeClick={handleNodeClick}
                      height={500}
                    />
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Learning Paths */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                学习路径设计
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                基于知识图谱设计的四条优化学习路径，帮助学生系统掌握财税大数据专业技能
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {learningPaths.map((path) => (
                <Card key={path.id} className="hover:shadow-lg transition-shadow duration-300">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 ${path.color} rounded-lg flex items-center justify-center text-white font-bold text-lg flex-shrink-0`}>
                      {path.id.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{path.name}</h3>
                      <p className="text-gray-600 mb-4">{path.description}</p>
                      
                      <div className="space-y-3">
                        {path.steps.map((step, index) => (
                          <div key={index} className="flex items-center gap-3">
                            <div className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center text-xs font-semibold text-gray-600">
                              {index + 1}
                            </div>
                            <span className="text-sm text-gray-700">{step}</span>
                            {index < path.steps.length - 1 && (
                              <i className="ri-arrow-right-line text-gray-400"></i>
                            )}
                          </div>
                        ))}
                      </div>

                      <div className="mt-6 pt-4 border-t border-gray-100">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setSelectedPath(selectedPath === path.id ? '' : path.id)}
                        >
                          <i className="ri-route-line mr-2"></i>
                          {selectedPath === path.id ? '收起路径' : '查看详细路径'}
                        </Button>
                      </div>

                      {selectedPath === path.id && (
                        <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                          <h4 className="font-semibold text-blue-900 mb-2">路径详情</h4>
                          <p className="text-sm text-blue-700">
                            该学习路径包含 {path.steps.length} 个核心模块，预计学习时长 {path.steps.length * 2} 周。
                            建议按照顺序逐步学习，每个模块完成后进行实践练习。
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Course Groups */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                专业课程群组
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                六大课程群组，涵盖财税大数据专业的核心知识领域
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  name: '会计基础团队',
                  core: '会计基础',
                  courses: ['税务会计', '管理会计', '行政事业单位会计', '企业财务管理', '财务审计'],
                  color: 'bg-blue-500',
                  icon: 'ri-calculator-line'
                },
                {
                  name: '税务专业小组',
                  core: '税法、税务会计',
                  courses: ['纳税筹划合规', '纳税检查', '纳税申报实训', '税收征管流程实训', '智慧化税费申报与管理'],
                  color: 'bg-green-500',
                  icon: 'ri-government-line'
                },
                {
                  name: '财务管理小组',
                  core: '企业财务管理',
                  courses: ['管理会计', '企业内部控制与风险管理', '财务审计'],
                  color: 'bg-purple-500',
                  icon: 'ri-line-chart-line'
                },
                {
                  name: '数字化技能群',
                  core: '大数据技术在财务中的应用',
                  courses: ['Excel在财务中的应用', '财税大数据分析'],
                  color: 'bg-teal-500',
                  icon: 'ri-database-2-line'
                },
                {
                  name: '智能财税群体',
                  core: '智能财税',
                  courses: ['财税机器人开发与维护', '税务管理', '业财管理信息系统应用'],
                  color: 'bg-orange-500',
                  icon: 'ri-robot-line'
                },
                {
                  name: '劳动教育群体',
                  core: '新时代大学生劳动教育',
                  courses: ['劳动观念', '劳动技能'],
                  color: 'bg-red-500',
                  icon: 'ri-tools-line'
                }
              ].map((group, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow duration-300">
                  <div className={`w-12 h-12 ${group.color} rounded-lg flex items-center justify-center text-white mb-4`}>
                    <i className={`${group.icon} text-xl`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{group.name}</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    <strong>核心要点：</strong>{group.core}
                  </p>
                  <div className="space-y-2">
                    <h4 className="font-semibold text-gray-700 text-sm">相关课程：</h4>
                    <ul className="space-y-1">
                      {group.courses.map((course, courseIndex) => (
                        <li key={courseIndex} className="text-sm text-gray-600 flex items-center gap-2">
                          <div className="w-1.5 h-1.5 bg-gray-400 rounded-full"></div>
                          {course}
                        </li>
                      ))}
                    </ul>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />

      {/* 节点详情弹窗 */}
      {selectedNode && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setSelectedNode(null)}>
          <div className="bg-white rounded-xl p-6 max-w-md mx-4 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900">{selectedNode.name}</h3>
                <span className="inline-block px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full mt-1">
                  {categories[selectedNode.category]?.name}
                </span>
              </div>
              <button
                onClick={() => setSelectedNode(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            
            <p className="text-gray-600 text-sm leading-relaxed mb-4">
              {selectedNode.description}
            </p>
            
            <div className="flex items-center justify-between text-xs text-gray-500">
              <span>重要程度: {selectedNode.value}/6</span>
              <span>知识层级: L{selectedNode.value}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
